import React, { useState, useEffect } from "react";
import { FiSearch, FiRefreshCw, FiDownload, FiX, FiEye, FiCheckCircle, FiClipboard, FiFileText } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/medicalappointments.css";
import { createPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';
import { db, auth } from "../../firebase";
import { collection, query, where, orderBy, onSnapshot, getDoc, doc, updateDoc, limit } from "firebase/firestore";
import Banner from "./Banner";
import MedicalEscalationForm from "./MedicalEscalationForm";
import Portal from "../../components/Portal";

const TOPIC_OPTIONS = [
  "HIV Awareness",
  "HIV Screening/Testing",
  "Birth Control/Implant",
  "Contraception",
  "Family Planning",
  "Gender-Based Violence",
  "Menstrual Health & Hygiene"
];

const REASON_OPTIONS = [
  "Medical advice needed",
  "Possible symptoms or health concern",
  "Medication/treatment-related question",
  "Urgent concern",
  "Sensitive/personal situation",
  "Unable to provide sufficient information"
];

const getStatusLabel = (status, t) => {
  const mapping = {
    'pending-review': t.pending,
    'under-assessment': t.underAssessment,
    'resolved': t.resolved
  };
  return mapping[status] || status;
};

const getStatusBadgeClass = (status) => {
  return `escalation-status-${status}`;
};

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting }) {
  const [selectedFormat, setSelectedFormat] = React.useState(null);

  React.useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

const MedicalEscalatedCases = () => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, false, false, true);
  
  const STATUS_FILTER_TABS = [
    { label: t.allStatus, value: "all" },
    { label: t.pending, value: "pending-review" },
    { label: t.underAssessment, value: "under-assessment" },
    { label: t.resolved, value: "resolved" }
  ];
  
  const [escalatedCases, setEscalatedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("error");
  const [selectedCase, setSelectedCase] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  // Fetch escalated chats with real-time updates and server-side filtering - OPTIMIZED
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      return;
    }

    let mounted = true;
    let pendingCasesLoaded = false;
    let assignedCasesLoaded = false;
    setLoading(true);

    // Query 1: Fetch pending-review cases (all professionals see these)
    const q1 = query(
      collection(db, "escalatedChats"),
      where("escalationStatus", "==", "pending-review"),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    // Query 2: Fetch resolved and under-assessment cases for current user
    // Note: This compound query requires a Firestore composite index
    const q2 = query(
      collection(db, "escalatedChats"),
      where("proId", "==", user.uid),
      where("escalationStatus", "in", ["resolved", "under-assessment"]),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const unsubscribe1 = onSnapshot(q1, (snapshot1) => {
      if (!mounted) return;
      
      const casesData = [];
      const seenIds = new Set();

      snapshot1.forEach(doc => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          casesData.push({
            id: doc.id,
            ...doc.data()
          });
        }
      });

      setEscalatedCases(prevCases => {
        // Merge with existing cases and deduplicate
        const merged = [...casesData];
        prevCases.forEach(c => {
          if (!seenIds.has(c.id)) {
            merged.push(c);
          }
        });
        return merged.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        });
      });
      pendingCasesLoaded = true;
      if (assignedCasesLoaded) setLoading(false);
    }, (error) => {
      console.error("Error fetching pending-review cases:", error);
      pendingCasesLoaded = true;
      if (assignedCasesLoaded) setLoading(false);
    });

    const unsubscribe2 = onSnapshot(q2, (snapshot2) => {
      if (!mounted) return;
      
      const casesData = [];
      const seenIds = new Set();

      snapshot2.forEach(doc => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          casesData.push({
            id: doc.id,
            ...doc.data()
          });
        }
      });

      setEscalatedCases(prevCases => {
        // Merge with existing cases and deduplicate
        const merged = [...casesData];
        prevCases.forEach(c => {
          if (!seenIds.has(c.id)) {
            merged.push(c);
          }
        });
        return merged.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        });
      });
      assignedCasesLoaded = true;
      if (pendingCasesLoaded) setLoading(false);
    }, (error) => {
      console.error("Error fetching user's resolved/under-assessment cases:", error);
      assignedCasesLoaded = true;
      if (pendingCasesLoaded) setLoading(false);
    });

    return () => {
      mounted = false;
      unsubscribe1();
      unsubscribe2();
    };
  }, []);

  // Filter cases
  const filteredCases = escalatedCases.filter(c => {
    const matchesSearch = !search || 
      c.ticketNumber?.toLowerCase().includes(search.toLowerCase()) ||
      c.escalationTopic?.toLowerCase().includes(search.toLowerCase()) ||
      c.escalationReason?.toLowerCase().includes(search.toLowerCase());
    
    const matchesTopic = !topicFilter || c.escalationTopic === topicFilter;
    
    const matchesReason = !reasonFilter || c.escalationReason === reasonFilter;
    
    const matchesStatus = statusFilter === "all" || c.escalationStatus === statusFilter;

    return matchesSearch && matchesTopic && matchesReason && matchesStatus;
  });

  // Get status tab count
  const getStatusTabCount = (value) => {
    if (value === "all") return escalatedCases.length;
    return escalatedCases.filter(c => c.escalationStatus === value).length;
  };

  // Client-side pagination
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCases = filteredCases.slice(startIndex, endIndex);

  const handleRefresh = () => {
    // Real-time listeners will automatically refresh
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  };

  const exportToPDF = async () => {
    const pdf = createPDF({
      title: "Escalated Cases Report"
    });

    let startY = pdf.contentStartY;

    // Add summary
    pdf.doc.setFontSize(10);
    pdf.doc.text(`Total Cases: ${filteredCases.length}`, pdf.margin, startY);
    startY += 15;

    // Separate data by status
    const pendingItems = filteredCases.filter(item => item.escalationStatus === 'pending-review');
    const underAssessmentItems = filteredCases.filter(item => item.escalationStatus === 'under-assessment');
    const resolvedItems = filteredCases.filter(item => item.escalationStatus === 'resolved');

    // Add pending items table if any
    if (pendingItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('PENDING REVIEW', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Ticket Number', 'Topic', 'Reason', 'Date Escalated'];
      const rows = pendingItems.map(item => [
        item.ticketNumber || 'N/A',
        item.escalationTopic || 'N/A',
        item.escalationReason || 'N/A',
        item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add under assessment items table if any
    if (underAssessmentItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('UNDER ASSESSMENT', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Ticket Number', 'Topic', 'Reason', 'Date Escalated'];
      const rows = underAssessmentItems.map(item => [
        item.ticketNumber || 'N/A',
        item.escalationTopic || 'N/A',
        item.escalationReason || 'N/A',
        item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add resolved items table if any
    if (resolvedItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('RESOLVED', pdf.margin, startY);
      startY += 10;
      
      const headers = ['Ticket Number', 'Topic', 'Reason', 'Date Escalated', 'Date Resolved'];
      const rows = resolvedItems.map(item => [
        item.ticketNumber || 'N/A',
        item.escalationTopic || 'N/A',
        item.escalationReason || 'N/A',
        item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A',
        item.resolvedAt ? (item.resolvedAt.toDate ? new Date(item.resolvedAt.toDate()).toLocaleDateString() : new Date(item.resolvedAt).toLocaleDateString()) : 'N/A'
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
    }

    pdf.save(`escalated-cases-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async () => {
    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ['ESCALATED CASES REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter],
      [],
      ['CASES SUMMARY'],
      ['Total Cases', filteredCases.length],
      ['Pending Review', filteredCases.filter(item => item.escalationStatus === 'pending-review').length],
      ['Under Assessment', filteredCases.filter(item => item.escalationStatus === 'under-assessment').length],
      ['Resolved', filteredCases.filter(item => item.escalationStatus === 'resolved').length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Separate data by status
    const pendingItems = filteredCases.filter(item => item.escalationStatus === 'pending-review');
    const underAssessmentItems = filteredCases.filter(item => item.escalationStatus === 'under-assessment');
    const resolvedItems = filteredCases.filter(item => item.escalationStatus === 'resolved');

    const contentData = [
      ['CASE DETAILS'],
    ];

    // Add pending items
    if (pendingItems.length > 0) {
      contentData.push(['PENDING REVIEW']);
      contentData.push(['Ticket Number', 'Topic', 'Reason', 'Date Escalated']);
      pendingItems.forEach(item => {
        contentData.push([
          item.ticketNumber || 'N/A',
          item.escalationTopic || 'N/A',
          item.escalationReason || 'N/A',
          item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    // Add under assessment items
    if (underAssessmentItems.length > 0) {
      contentData.push([]);
      contentData.push(['UNDER ASSESSMENT']);
      contentData.push(['Ticket Number', 'Topic', 'Reason', 'Date Escalated']);
      underAssessmentItems.forEach(item => {
        contentData.push([
          item.ticketNumber || 'N/A',
          item.escalationTopic || 'N/A',
          item.escalationReason || 'N/A',
          item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    // Add resolved items
    if (resolvedItems.length > 0) {
      contentData.push([]);
      contentData.push(['RESOLVED']);
      contentData.push(['Ticket Number', 'Topic', 'Reason', 'Date Escalated', 'Date Resolved']);
      resolvedItems.forEach(item => {
        contentData.push([
          item.ticketNumber || 'N/A',
          item.escalationTopic || 'N/A',
          item.escalationReason || 'N/A',
          item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A',
          item.resolvedAt ? (item.resolvedAt.toDate ? new Date(item.resolvedAt.toDate()).toLocaleDateString() : new Date(item.resolvedAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    const contentSheet = XLSX.utils.aoa_to_sheet(contentData);
    XLSX.utils.book_append_sheet(workbook, contentSheet, 'Cases');

    XLSX.writeFile(workbook, `escalated-cases-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async () => {
    let csvData = [
      ['ESCALATED CASES REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter],
      [],
      ['CASES SUMMARY'],
      ['Total Cases', filteredCases.length],
      ['Pending Review', filteredCases.filter(item => item.escalationStatus === 'pending-review').length],
      ['Under Assessment', filteredCases.filter(item => item.escalationStatus === 'under-assessment').length],
      ['Resolved', filteredCases.filter(item => item.escalationStatus === 'resolved').length],
      [],
      ['CASE DETAILS'],
    ];

    // Separate data by status
    const pendingItems = filteredCases.filter(item => item.escalationStatus === 'pending-review');
    const underAssessmentItems = filteredCases.filter(item => item.escalationStatus === 'under-assessment');
    const resolvedItems = filteredCases.filter(item => item.escalationStatus === 'resolved');

    // Add pending items
    if (pendingItems.length > 0) {
      csvData.push(['PENDING REVIEW']);
      csvData.push(['Ticket Number', 'Topic', 'Reason', 'Date Escalated']);
      pendingItems.forEach(item => {
        csvData.push([
          item.ticketNumber || 'N/A',
          item.escalationTopic || 'N/A',
          item.escalationReason || 'N/A',
          item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    // Add under assessment items
    if (underAssessmentItems.length > 0) {
      csvData.push([]);
      csvData.push(['UNDER ASSESSMENT']);
      csvData.push(['Ticket Number', 'Topic', 'Reason', 'Date Escalated']);
      underAssessmentItems.forEach(item => {
        csvData.push([
          item.ticketNumber || 'N/A',
          item.escalationTopic || 'N/A',
          item.escalationReason || 'N/A',
          item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    // Add resolved items
    if (resolvedItems.length > 0) {
      csvData.push([]);
      csvData.push(['RESOLVED']);
      csvData.push(['Ticket Number', 'Topic', 'Reason', 'Date Escalated', 'Date Resolved']);
      resolvedItems.forEach(item => {
        csvData.push([
          item.ticketNumber || 'N/A',
          item.escalationTopic || 'N/A',
          item.escalationReason || 'N/A',
          item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A',
          item.resolvedAt ? (item.resolvedAt.toDate ? new Date(item.resolvedAt.toDate()).toLocaleDateString() : new Date(item.resolvedAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `escalated-cases-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      setSelectedExportFormat(format);
      
      if (format === 'pdf') {
        await exportToPDF();
      } else if (format === 'excel') {
        await exportToExcel();
      } else if (format === 'csv') {
        await exportToCSV();
      }
    } catch (error) {
      console.error('Export error:', error);
      setBannerMessage('Export failed');
      setBannerType('error');
    } finally {
      setIsExporting(false);
      setSelectedExportFormat(null);
      setIsExportModalOpen(false);
    }
  };

  const handleStatusUpdate = async (caseId, newStatus) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setBannerMessage("User not authenticated");
        setBannerType("error");
        return;
      }

      const updateData = {
        escalationStatus: newStatus
      };

      // When accepting (pending-review -> under-assessment), add proName and proId
      if (newStatus === 'under-assessment') {
        updateData.proId = user.uid;
        // Try to get proName from users collection where accountType is staff
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            updateData.proName = userData.fullName || user.email || "Unknown";
          } else {
            updateData.proName = user.email || "Unknown";
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          updateData.proName = user.email || "Unknown";
        }
      }

      await updateDoc(doc(db, "escalatedChats", caseId), updateData);
      
      // Also update the chat if it exists
      const caseData = escalatedCases.find(c => c.id === caseId);
      if (caseData?.chatId) {
        await updateDoc(doc(db, "chats", caseData.chatId), {
          escalationStatus: newStatus
        });
      }
      
      setBannerMessage(`Status updated to ${getStatusLabel(newStatus, t)}`);
      setBannerType("success");
    } catch (error) {
      console.error("Error updating status:", error);
      setBannerMessage("Failed to update status");
      setBannerType("error");
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit',
      minute: '2-digit'
    });
    return (
      <>
        <div>{dateStr}</div>
        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{timeStr}</div>
      </>
    );
  };

  return (
    <div className="ma-main review-appointments-panel">
      {selectedCase ? (
        <MedicalEscalationForm 
          caseData={selectedCase} 
          onBack={() => setSelectedCase(null)}
          readOnly={selectedCase.escalationStatus === "resolved"}
        />
      ) : (
        <>
          {bannerMessage && (
            <Banner 
              message={bannerMessage} 
              type={bannerType} 
              onClose={() => setBannerMessage("")}
            />
          )}
      
      <ExportModal 
        isOpen={isExportModalOpen} 
        onClose={() => setIsExportModalOpen(false)} 
        onExport={handleExport} 
        isExporting={isExporting} 
      />

      {/* Header */}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Medical Professional - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Medical Professional
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Escalated Cases
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Review and manage cases escalated from peer educator chats
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      {/* Filters */}
      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="text"
            placeholder="Search inquiries..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>

        <select
          className="admin-aura-select"
          value={topicFilter}
          onChange={(e) => {
            setTopicFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">{t.allStatus}</option>
          {TOPIC_OPTIONS.map(topic => (
            <option key={topic} value={topic}>{topic}</option>
          ))}
        </select>

        <select
          className="admin-aura-select"
          value={reasonFilter}
          onChange={(e) => {
            setReasonFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">{t.allStatus}</option>
          {REASON_OPTIONS.map(reason => (
            <option key={reason} value={reason}>{reason}</option>
          ))}
        </select>

        <button 
          onClick={handleRefresh}
          className="admin-primary-gradient-btn"
        >
          <FiRefreshCw />
          {t.refresh}
        </button>
        <button
          onClick={() => setIsExportModalOpen(true)}
          className="admin-primary-gradient-btn"
        >
          <FiDownload />
          {t.exportReport || "Export Report"}
        </button>
      </div>

      {/* Status Tabs Row with Export PDF and Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div className="floating-index-tabs ma-status-tabs" aria-label="Filter escalated cases by status" style={{ display: "flex", gap: "8px" }}>
          {STATUS_FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`floating-tab ${statusFilter === tab.value ? "active floating-tab-active" : ""}`}
              onClick={() => {
                setStatusFilter(tab.value);
                setCurrentPage(1);
              }}
            >
              {tab.label}
              <span className="tab-count">{getStatusTabCount(tab.value)}</span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {filteredCases.length > 0 ? (
                `${startIndex + 1}-${Math.min(endIndex, filteredCases.length)} of ${filteredCases.length}`
              ) : (
                '0 of 0'
              )}
            </div>

            {/* Pagination Arrows */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                ←
              </button>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                →
              </button>
            </div>
        </div>
      </div>

      {/* Custom Tooltip for Action Buttons */}
      {tooltip.visible && (
        <div
          className={`admin-action-tooltip ${tooltip.type}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Table */}
      <div className="ma-table-container" style={{
        background: '#fff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        <table className="ma-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Status</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Ticket Number</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Topic</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Reason</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Date Escalated</th>
              {statusFilter === 'resolved' && <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Date Resolved</th>}
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 4) * 10}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>
                    {statusFilter === 'resolved' && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>}
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                  </tr>
                ))}
              </>
            ) : filteredCases.length === 0 ? (
              <tr>
                <td colSpan={statusFilter === 'resolved' ? 7 : 6} style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  No escalated cases found
                </td>
              </tr>
            ) :
              paginatedCases.map((c, index) => (
                  <tr 
                    key={c.id} 
                    style={{ 
                      borderBottom: '1px solid #f1f5f9', 
                      transition: 'background 0.2s',
                      cursor: 'default'
                    }} 
                    onMouseEnter={(e) => e.target.style.background = '#f8fafc'} 
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    <td style={{ padding: '16px' }}>
                      <span className={`ma-badge ${getStatusBadgeClass(c.escalationStatus)}`}>
                        {getStatusLabel(c.escalationStatus, t)}
                      </span>
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#374151', maxWidth: '150px', wordBreak: 'break-word', lineHeight: '1.4' }}>{c.ticketNumber || "N/A"}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>{c.escalationTopic || "N/A"}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#374151' }}>{c.escalationReason || "N/A"}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#64748b' }}>{formatTimestamp(c.createdAt)}</td>
                    {statusFilter === 'resolved' && <td style={{ padding: '16px', fontSize: '14px', color: '#64748b' }}>{formatTimestamp(c.resolvedAt)}</td>}
                    <td style={{ padding: '16px' }}>
                      {c.escalationStatus === 'pending-review' && (
                        <button
                          type="button"
                          className="ma-action-btn approve"
                          onClick={() => handleStatusUpdate(c.id, 'under-assessment')}
                          style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6' }}
                          onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; handleActionMouseEnter(e, "Accept Case", 'approve'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; handleActionMouseLeave(); }}
                        >
                          <FiCheckCircle size={16} />
                        </button>
                      )}
                      {c.escalationStatus === 'under-assessment' && (
                        <button
                          type="button"
                          className="ma-action-btn view"
                          onClick={() => setSelectedCase(c)}
                          style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                          onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "Review Case", 'reschedule'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                        >
                          <FiClipboard size={16} />
                        </button>
                      )}
                      {c.escalationStatus === 'resolved' && (
                        <button
                          type="button"
                          className="ma-action-btn view"
                          onClick={() => setSelectedCase(c)}
                          style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6' }}
                          onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; handleActionMouseEnter(e, "View Chat", 'view'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; handleActionMouseLeave(); }}
                        >
                          <FiEye size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
};

export default MedicalEscalatedCases;
